import React, { useContext } from "react";
import { Field, Form } from "react-final-form";
import createDecorator from "final-form-focus";
import {
  Box,
  Paper,
  makeStyles,
  Typography,
  FormControlLabel,
  Switch,
  Link
} from "@material-ui/core";
import { Trans, useTranslation } from "react-i18next";
import { ButtonLoader, IntroText, Loader } from "../../../components";
import { string, object } from "yup";
import { useYupValidation } from "../../../hooks";
import MuiPhoneNumber from "material-ui-phone-number";
import { COUNTRIES_ISO_CODES } from "../../../constants";
import { BrandThemeContext } from "../../../contexts/BrandThemeProvider";

const focusOnErrors = createDecorator();

const ALLOWED_PHONE_COUNTRIES = [
  COUNTRIES_ISO_CODES.US,
  COUNTRIES_ISO_CODES.MX
];

const validationSchema = object({
  phone: string()
    .required()
    .min(12)
    .trim()
    .label("Phone")
});

const useClasses = makeStyles(theme => ({
  smallText: {
    fontSize: "13px"
  },
  phoneNumberInput: {
    marginBottom: theme.spacing(2),
    "& button.MuiPhoneNumber-flagButton": {
      "&::after": {
        content: "''",
        display: "block",
        width: 0,
        height: 0,
        borderLeft: "4px solid transparent",
        borderRight: "4px solid transparent",
        borderTop: `4px solid ${theme.palette.grey["600"]}`,
        marginLeft: "3px"
      }
    }
  }
}));

const LoginForm = ({ onSubmit, formSubmitting }) => {
  const { t } = useTranslation();
  const classes = useClasses();
  const { validate } = useYupValidation();
  const { themeName } = useContext(BrandThemeContext);

  const validateForm = async data => {
    const errors = await validate(validationSchema)(data);

    if (errors) return errors;

    onSubmit(data);
  };

  if (formSubmitting) return <Loader />;

  return (
    <Box mx={2} data-cy="schedule-login">
      <Paper>
        <Box p={2} pt={3}>
          <IntroText>{t("auth.login.intro_text")}</IntroText>
          <Typography paragraph align="center">
            {t("auth.login.secondary_text")}
          </Typography>
          <Form
            onSubmit={validateForm}
            decorators={[focusOnErrors]}
            initialValues={{ has_sms: true }}
            render={({ handleSubmit, submitErrors: errors = {} }) => (
              <form onSubmit={handleSubmit}>
                <Field
                  name="phone"
                  parse={value => value.replace(/[^+\d]+/g, "")}
                  format={value => value}
                  render={({ input }) => (
                    <MuiPhoneNumber
                      {...input}
                      defaultCountry={COUNTRIES_ISO_CODES.US.toLowerCase()}
                      onlyCountries={ALLOWED_PHONE_COUNTRIES.map(countryCode =>
                        countryCode.toLowerCase()
                      )}
                      disableAreaCodes={true}
                      data-cy="input-login-phone"
                      autoFocus
                      margin="none"
                      variant="outlined"
                      label={t("auth.login.form.phone_number.label")}
                      fullWidth
                      error={!!errors.phone}
                      helperText={errors.phone}
                      className={classes.phoneNumberInput}
                    />
                  )}
                />
                <FormControlLabel
                  labelPlacement="start"
                  control={
                    <Field
                      name="has_sms"
                      type="checkbox"
                      render={({ input }) => (
                        <Switch
                          {...input}
                          inputProps={{ "data-cy": "input-has_sms" }}
                          color="primary"
                        />
                      )}
                    />
                  }
                  label={t("auth.login.form.has_sms.label")}
                />
                <Box mt={2} mb={3}>
                  <ButtonLoader
                    data-cy="button-login-submit"
                    type="submit"
                    variant="contained"
                    fullWidth
                    loading={formSubmitting}
                    color="primary"
                    size="large"
                  >
                    {t("common.next")}
                  </ButtonLoader>
                </Box>
                <Typography align="center" className={classes.smallText}>
                  <Trans i18nKey={themeName === "GGT" ? "auth.login.termsGGT" : "auth.login.termsGGV"}>
                    By clicking "next", you agree to GoGet, Inc{" "}
                    <Link
                      href="https://gogetdoc.com/terms"
                      target="_blank"
                      rel="noopener"
                      color="inherit"
                      underline="always"
                    >
                      Terms of Service
                    </Link>{" "}
                    and acknowledge you have read the{" "}
                    <Link
                      href="https://gogetdoc.com/privacy"
                      target="_blank"
                      rel="noopener"
                      color="inherit"
                      underline="always"
                    >
                      Privacy Policy
                    </Link>
                    . You also consent to receive calls or SMS messages,
                    including by automated dialer, from GoGet, Inc and its
                    affiliates to the number you provide for informational
                    purposes including your appointments and results. You
                    understand that you may opt out by texting{" "}
                    <strong>STOP</strong> to 877-280-2151
                  </Trans>
                </Typography>
              </form>
            )}
          />
        </Box>
      </Paper>
    </Box>
  );
};

export { LoginForm };
